import { css, type FlattenSimpleInterpolation } from 'styled-components';
import { FOCUS_INDICATOR } from '#/user/themes';

interface FocusStyleParams {
  /**
   * Whether the focus indicator should be around the element or inset into it
   * Use default unless the indicator is being cut off on one or more sides
   */
  type: 'default' | 'inset';
  /** Make outline property important; should primarily be used to override 3p library styles */
  important?: boolean;
}

// Prefer focusCssRule; only import focusStyle if you have to add focus styles to a custom 3p library focus class
export const focusStyle = ({
  type,
  important = false,
}: FocusStyleParams): FlattenSimpleInterpolation => css`
  outline: 2px ${FOCUS_INDICATOR.inner} solid ${important ? ' !important' : ''};
  outline-offset: ${type === 'inset' ? '-4px' : '0'}${important ? ' !important' : ''};
  box-shadow: ${type === 'inset' ? 'inset ' : ''}0 0 0 4px
    ${FOCUS_INDICATOR.outer}${important ? ' !important' : ''};
`;

// Generally we should rely on the default focus rules in our global styles
// The primary use case for importing this is needing to inset the focus indicator because it's being cut off
export const focusCssRule = ({
  type,
  important = false,
}: FocusStyleParams): FlattenSimpleInterpolation => css`
  // Fallback for older browsers that don't support :focus-visible
  &:focus {
    ${focusStyle({ type, important })}
  }

  // box-shadow doesn't work on text inputs in Safari <= 16 without this
  // to confirm this is working, focus an input and make sure you see the black box-shadow
  &input:focus,
  &textarea:focus {
    -webkit-appearance: none;
  }

  // If we do support :focus-visible, remove the default focus indicator
  &:focus:not(:focus-visible) {
    outline: none ${important ? ' !important' : ''};
    box-shadow: none ${important ? ' !important' : ''};
  }

  // Now re-include the focus styles for :focus-visible if it's supported
  &:focus-visible {
    ${focusStyle({ type, important })}
  }
`;
